import { useState, useMemo } from 'react';
import { pdf } from '@react-pdf/renderer';
import { useConfigStore } from '@/store/useConfigStore';
import { InputField } from '@/components/ui/InputField';
import { FileText, Download, CheckCircle2, Loader2, RotateCcw } from 'lucide-react';
import { OrderConfirmationPDF } from '@/pdf/OrderConfirmationPDF';
import { PurchaseOrderPDF } from '@/pdf/PurchaseOrderPDF';
import { CutSheetPDF } from '@/pdf/CutSheetPDF';

export function Step5Approval() {
  const { calculation, windowConfig, styleConfig, fabricConfig, liningConfig, hardwareConfig } = useConfigStore();
  const resetAll = useConfigStore(state => state.resetAll);
  
  const [customerName, setCustomerName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [approved, setApproved] = useState(false);
  const [approvedAt, setApprovedAt] = useState<Date | null>(null);
  const [generating, setGenerating] = useState<string | null>(null);

  const orderNumber = useMemo(() => `ORD-${Math.floor(Math.random() * 1000000)}`, []);

  const handleApprove = () => {
    if (customerName && agreed) {
      setApproved(true);
      setApprovedAt(new Date());
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset and start a new order? This will clear all data.')) {
      if (resetAll) resetAll();
      else window.location.reload();
    }
  };

  if (!calculation) {
    return <div className="p-8 text-center text-slate-500">No calculation data found. Please return to previous steps.</div>;
  }

  // Derive costs
  const fabricCost = calculation.totalFabricMeters * (fabricConfig.pricePerMeter || 0);
  const liningCost = calculation.liningMeters * (liningConfig.pricePerMeter || 10);
  const hwCost = (hardwareConfig.pricePerMeter || 0) + (hardwareConfig.accessoriesPrice || 0);
  const totalCost = fabricCost + liningCost + hwCost;

  const sharedProps = {
    customerName,
    orderNumber,
    windowConfig,
    styleConfig,
    fabricConfig,
    liningConfig,
    hardwareConfig,
    calculation,
    fabricCost,
    liningCost,
    hardwareCost: hwCost,
    totalCost,
  };

  /** Generate a PDF blob and trigger a browser download */
  const handleDownload = async (type: 'confirmation' | 'purchase-order' | 'cut-sheet') => {
    if (!approvedAt) return;
    setGenerating(type);

    try {
      let doc: any;
      let filename: string;

      switch (type) {
        case 'confirmation':
          doc = <OrderConfirmationPDF {...sharedProps} approvedAt={approvedAt} />;
          filename = `HailMary_OrderConfirmation_${customerName.replace(/\\s+/g, '_')}.pdf`;
          break;
        case 'purchase-order':
          doc = <PurchaseOrderPDF {...sharedProps} />;
          filename = `HailMary_PurchaseOrder_${customerName.replace(/\\s+/g, '_')}.pdf`;
          break;
        case 'cut-sheet':
          doc = (
            <CutSheetPDF
              customerName={customerName}
              windowConfig={windowConfig}
              styleConfig={styleConfig}
              fabricConfig={fabricConfig}
              calculation={calculation}
            />
          );
          filename = `HailMary_CutSheet_${customerName.replace(/\\s+/g, '_')}.pdf`;
          break;
        default:
          return;
      }

      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(`Failed to generate ${type} PDF:`, err);
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Order Approval</h2>
          <p className="text-slate-600">Review the final order details and generate production documents.</p>
        </div>
        <button 
          onClick={handleReset}
          className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors font-medium border border-red-200"
        >
          <RotateCcw size={16} />
          Reset App
        </button>
      </div>

      {/* Condensed Summary */}
      <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 flex flex-col md:flex-row justify-between gap-6">
        <div>
          <p className="text-sm text-slate-500 mb-1">Total Fabric Required</p>
          <p className="text-xl font-bold text-slate-900">{calculation.totalFabricMeters.toFixed(2)} m</p>
        </div>
        <div>
          <p className="text-sm text-slate-500 mb-1">Total Panels</p>
          <p className="text-xl font-bold text-slate-900">{calculation.panelBreakdown.length}</p>
        </div>
        <div>
          <p className="text-sm text-slate-500 mb-1">Estimated Cost</p>
          <p className="text-xl font-bold text-amber-600">${totalCost.toFixed(2)}</p>
        </div>
      </div>

      {!approved ? (
        <div className="space-y-6 bg-white p-6 rounded-xl border border-slate-200">
          <InputField 
            label="Customer Name / Reference" 
            placeholder="Enter customer name..." 
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
          />

          <label className="flex items-start gap-3 cursor-pointer group">
            <div className="relative flex items-center justify-center mt-1">
              <input 
                type="checkbox" 
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="peer appearance-none w-5 h-5 border-2 border-slate-300 rounded cursor-pointer checked:border-amber-500 checked:bg-amber-500 transition-colors"
              />
              <CheckCircle2 size={14} className="absolute text-white opacity-0 peer-checked:opacity-100 pointer-events-none" />
            </div>
            <span className="text-sm text-slate-600 leading-relaxed group-hover:text-slate-900 transition-colors">
              I have reviewed the measurements, style selections, and material requirements. I approve this configuration for production. I understand that custom made items cannot be returned.
            </span>
          </label>

          <button
            onClick={handleApprove}
            disabled={!customerName || !agreed}
            className="w-full py-4 rounded-xl bg-amber-500 text-slate-900 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-amber-400 active:bg-amber-600 transition-all shadow-md hover:shadow-lg disabled:shadow-none"
          >
            Approve & Generate Documents
          </button>
        </div>
      ) : (
        <div className="space-y-6 animate-in zoom-in-95 duration-500">
          <div className="bg-green-50 border border-green-200 p-6 rounded-xl flex items-center gap-4">
            <div className="bg-green-500 text-white rounded-full p-2">
              <CheckCircle2 size={24} />
            </div>
            <div>
              <h3 className="font-bold text-green-900">Order Approved</h3>
              <p className="text-sm text-green-700">
                Approved by {customerName} on {approvedAt?.toLocaleDateString()} at {approvedAt?.toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* PDF Downloads section - always visible but disabled if not approved */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Order Confirmation */}
        <button 
          onClick={() => handleDownload('confirmation')}
          disabled={!approved || generating !== null}
          className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 transition-all group disabled:opacity-60"
        >
          <div className="bg-slate-100 text-slate-600 p-2 rounded-lg group-hover:bg-slate-200 transition-colors">
            {generating === 'confirmation' ? <Loader2 size={20} className="animate-spin" /> : <FileText size={20} />}
          </div>
          <div className="text-left flex-1">
            <p className="font-semibold text-slate-900">Order Confirmation</p>
            <p className="text-xs text-slate-500">Customer copy</p>
          </div>
          <Download size={18} className="text-slate-400 group-hover:text-slate-600" />
        </button>

        {/* Purchase Order */}
        <button 
          onClick={() => handleDownload('purchase-order')}
          disabled={!approved || generating !== null}
          className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 transition-all group disabled:opacity-60"
        >
          <div className="bg-slate-100 text-slate-600 p-2 rounded-lg group-hover:bg-slate-200 transition-colors">
            {generating === 'purchase-order' ? <Loader2 size={20} className="animate-spin" /> : <FileText size={20} />}
          </div>
          <div className="text-left flex-1">
            <p className="font-semibold text-slate-900">Purchase Order</p>
            <p className="text-xs text-slate-500">Supplier copy</p>
          </div>
          <Download size={18} className="text-slate-400 group-hover:text-slate-600" />
        </button>

        {/* Cut Sheet */}
        <button 
          onClick={() => handleDownload('cut-sheet')}
          disabled={!approved || generating !== null}
          className="flex items-center gap-3 p-4 rounded-xl border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-all group shadow-sm disabled:opacity-60"
        >
          <div className="bg-amber-200 text-amber-700 p-2 rounded-lg transition-colors">
            {generating === 'cut-sheet' ? <Loader2 size={20} className="animate-spin" /> : <FileText size={20} />}
          </div>
          <div className="text-left flex-1">
            <p className="font-semibold text-amber-900">Cut Sheet</p>
            <p className="text-xs text-amber-700">Workroom copy</p>
          </div>
          <Download size={18} className="text-amber-600 group-hover:text-amber-800" />
        </button>
      </div>
    </div>
  );
}
